import {
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import api from "../../../services/api";
import { useEffect, useRef, useState } from "react";
import { useNotification } from "../../../componentes/notificationProvider";
import { AxiosError } from "axios";
import { useOutletContext } from "react-router-dom";
import { ReactComponent as EmptyBox } from "../../../assets/icons/emptybox.svg";
import { Add } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { Socket } from "socket.io-client";
import DraggableFilter from "./draggableFilter";
import { LoadingPage } from "../../loading";
import { alpha, minWidth, Stack } from "@mui/system";

interface PageName {
  id: string;
  title: string;
}

interface FiltersResponse {
  filters: Filters[];
}

type OutletContextType = {
  setSubTitle: (subtitle: string) => void;
  setIsFilterEditor: React.Dispatch<React.SetStateAction<boolean>>;
  setPageId: React.Dispatch<React.SetStateAction<string>>;
};

export default function FiltersConfig({ socket }: { socket: Socket }) {
  const { setSubTitle, setIsFilterEditor, setPageId } =
    useOutletContext<OutletContextType>();

  const [pagesName, setPagesName] = useState<PageName[]>([]);
  const [selectedPageId, setSeletedPageId] = useState<string>("");
  const [pageFilters, setPageFilters] = useState<Filters[]>([]);
  const [loadingPage, setLoadingPage] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const notify = useNotification();
  const params = useParams();
  const navigate = useNavigate();
  const styles = {
    boxStacks: {
      p: 2,
      gap: 2,
      backgroundColor: theme.palette.background.paper,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "end",
      width: "100%",
      minWidth: "360px",
    },
    container: {
      display: "flex",
      flexDirection: "column",
      padding: 3,
      m: 3,
      borderRadius: "1rem",
      gap: 2,
      backgroundColor: theme.palette.background.paper,
      minWidth: "360px",
    },
    textFieldAndButton: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 2,
    },
    boxDragAndDrop: {
      display: "flex",
      flexDirection: "column",
      borderRadius: "1rem",
      height: "50vh",
      overflowY: "scroll",
      overflowX: "auto",
      width: "100%",
    },
    boxNoFilters: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
    },
    emptyBox: { fill: theme.palette.cromatic.grey.light },

    typography: {
      fontSize: "1rem",
      color: theme.palette.text.primary,
    },
  };

  useEffect(() => {
    setSubTitle("Filtros");
  }, [setSubTitle]);

  useEffect(() => {
    setLoadingPage(true);
    const fetchData = async () => {
      try {
        const response = await api.get<PageName[]>("/pages/findNames");
        setPagesName(response.data);
      } catch (error) {
        if (error instanceof AxiosError)
          notify(error?.status || 500, error.message, "error", error);
        else notify("Não foi possível se conectar ao servidor!", "error");
      } finally {
        setLoadingPage(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (params.pageId) {
      setSeletedPageId(params.pageId);
      handleChange({
        target: { value: params.pageId },
      } as React.ChangeEvent<HTMLInputElement>);
      return;
    }
    if (pagesName.length > 0) {
      setSeletedPageId(pagesName[0].id);
      handleChange({
        target: { value: pagesName[0].id },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [params.pageId, pagesName]);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const response = await api.post<FiltersResponse | null>(
        "/filters/findFilters",
        {
          id: event.target.value as string,
        }
      );
      setPageFilters(response.data?.filters ?? []);
      setSeletedPageId(event.target.value);
    } catch (error) {
      if (error instanceof AxiosError)
        notify(error?.status || 500, error.message, "error", error);
      else notify("Não foi possível se conectar ao servidor!", "error");
    }
  };

  const handleDeleteFilter = async (variableName: string) => {
    try {
      const response = await api.post<FiltersResponse>(
        "/filters/updateFilter",
        {
          id: selectedPageId,
          filters: pageFilters,
          variableName: variableName,
        }
      );
      setPageFilters(response.data.filters);
    } catch (error) {
      if (error instanceof AxiosError)
        notify(error?.status || 500, error.message, "error", error);
      else notify("Não foi possível se conectar ao servidor!", "error");
    }
  };
  const handleReorderFilters = (fromIndex: number, toIndex: number) => {
    if (!socket || fromIndex === toIndex) {
      return;
    }

    try {
      const updatedFilters = [...pageFilters];
      const [movedFilter] = updatedFilters.splice(fromIndex, 1);
      updatedFilters.splice(toIndex, 0, movedFilter);

      const filtersWithUpdatedPosition = updatedFilters.map((f, i) => ({
        ...f,
        position: i,
      }));

      setPageFilters(filtersWithUpdatedPosition);

      socket.emit(
        "updatePositionFilters",
        {
          pageId: selectedPageId,
          filters: filtersWithUpdatedPosition,
        },
        (response: any) => {
          if (response.error) {
            setPageFilters([...pageFilters]);
            notify(
              response.status,
              "Error: Não foi possível atualizar o elemento",
              "error"
            );
          }
        }
      );
    } catch (error) {
      notify(
        (error as any)?.status || 500,
        "Não foi possível se conectar com o servidor",
        "error"
      );
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.textFieldAndButton}>
        <TextField
          select
          label="Página"
          variant="filled"
          value={selectedPageId}
          onChange={handleChange}
          sx={{
            width: "100%",
            maxWidth: 500,
          }}
        >
          {pagesName.map((page) => (
            <MenuItem key={page.id} value={page.id}>
              {page.title}
            </MenuItem>
          ))}
        </TextField>

        <Button
          variant="contained"
          sx={{
            display: { xs: "none", md: "flex" },
            backgroundColor: alpha(theme.palette.primary.main, 0.3),
            color: theme.palette.primary.main,
            height: "40px",
          }}
          startIcon={<Add />}
          disabled={!selectedPageId ? true : false}
          onClick={() => {
            setIsFilterEditor(true);
            setPageId(selectedPageId);
            navigate(`/configuracoes/filters/add/${selectedPageId}`);
          }}
        >
          Adicionar
        </Button>
        <Button
          variant="contained"
          sx={{
            display: { xs: "flex", md: "none" },
            backgroundColor: alpha(theme.palette.primary.main, 0.3),
            color: theme.palette.primary.main,
            height: "40px",
          }}
          disabled={!selectedPageId ? true : false}
          onClick={() => {
            setIsFilterEditor(true);
            setPageId(selectedPageId);
            navigate(`/configuracoes/filters/add/${selectedPageId}`);
          }}
        >
          <Add />
        </Button>
      </Box>
      <Divider sx={{ borderColor: theme.palette.stroke }} />
      {loadingPage ? (
        <Box sx={styles.boxDragAndDrop}>
          <LoadingPage />
        </Box>
      ) : (
        <Box sx={styles.boxDragAndDrop} key={"box-drop"} ref={boxRef}>
          {pagesName && pageFilters && pageFilters.length > 0 ? (
            <>
              <Grid container>
                <Box
                  sx={{
                    p: 1,
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                  }}
                >
                  <Grid size={1}></Grid>
                  <Grid size={2.75}>
                    <Typography sx={styles.typography}>Label</Typography>
                  </Grid>
                  <Grid size={2.75}>
                    <Typography sx={styles.typography}>Coluna</Typography>
                  </Grid>
                  <Grid size={2.75}>
                    <Typography sx={styles.typography}>Variável</Typography>
                  </Grid>
                  <Grid size={2.75}>
                    <Typography sx={styles.typography}>Ações</Typography>
                  </Grid>
                </Box>
              </Grid>

              {pageFilters.map((filter, index) => (
                <DraggableFilter
                  key={filter.variable}
                  filter={filter}
                  index={index}
                  onReorder={handleReorderFilters}
                  selectedPageId={selectedPageId}
                  handleDeleteFilter={handleDeleteFilter}
                  setIsFilterEditor={setIsFilterEditor}
                  setPageId={setPageId}
                  boxRef={boxRef}
                />
              ))}
            </>
          ) : (
            <Box sx={styles.boxNoFilters}>
              <EmptyBox style={styles.emptyBox} />
              <Typography color="textDisabled">{`Filtros não encontrados. `}</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
